<script setup lang="ts">
import { computed } from 'vue'
import type { PropType } from 'vue'
import type { Editor } from '@tiptap/vue-3'

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'

import clsx from 'clsx'

import { useEditorBetaStore } from '@/stores/editor/beta'

const betaStore = useEditorBetaStore()
const props = defineProps({
    editor: {
        type: Object as PropType<Editor>,
        required: true
    },
    isDark: {
        type: Boolean,
        required: true
    },
    backgroundColor: {
        type: String,
        required: true
    },
    inSidebar: {
        type: Boolean,
        default: false
    }
})

const defaultColors = [
    {
        value: '#B8B8B8',
        name: 'Cinza'
    },
    {
        value: '#E33838',
        name: 'Vermelho'
    },
    {
        value: '#E39F38',
        name: 'Laranja'
    },
    {
        value: '#E3DC38',
        name: 'Amarelo'
    },
    {
        value: '#50E338',
        name: 'Verde'
    },
    {
        value: '#3868E3',
        name: 'Azul'
    },
    {
        value: '#9838E3',
        name: 'Roxo'
    },
    {
        value: '#E338C8',
        name: 'Rosa'
    }
]

const colors = computed(() => {
    return [
        {
            value: '#FAFAFA',
            name: 'Branco'
        },
        {
            value: '#060606',
            name: 'Preto'
        },
        ...defaultColors,
    ]
})

const unsetColor = () => {
    betaStore.getTextEditorCommandChain(props.editor)?.unsetColor().run()
}

const setColor = (color: string) => {
    betaStore.getTextEditorCommandChain(props.editor)?.setColor(color).run()
}

const unsetHighlight = () => {
    betaStore.getTextEditorCommandChain(props.editor)?.unsetHighlight().run()
}

const setHighlight = (color: string) => {
    betaStore.getTextEditorCommandChain(props.editor)?.setHighlight({ color }).run()
}
</script>

<template>
    <popover class="relative" v-slot="{ open }">
        <popover-button :class="clsx('action-text-style', 'flex items-center')">
            <span class="sr-only">Text Style</span>

            <svg
                :class="clsx(
                    isDark ? 'stroke-[#FAFAFA]' : 'stroke-[#060606]'
                )"
                class="w-[25px] h-6"
                stroke="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 25 24"
                fill="none"
            >
                <path
                    d="M11.3866 8.06767L16.4323 13.1134M5.5 15.6362L15.4395 5.69667C16.3684 4.76778 17.8744 4.76778 18.8033 5.69666C19.7322 6.62555 19.7322 8.13157 18.8033 9.06046L8.86379 19H5.5V15.6362Z"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>

            <svg
                :class="
                    clsx(
                        open && 'rotate-180 transform',
                        isDark ? 'stroke-[#FAFAFA]' : 'stroke-[#060606]'
                    )
                "
                class="w-4 h-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
            >
                <path
                    d="M4 6L8.00065 10L12 6"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </popover-button>

        <popover-panel
            :style="{ backgroundColor: backgroundColor }"
            :class="
                clsx(
                    'absolute flex flex-col min-w-[200px] max-h-[230px] overflow-y-scroll p-4 space-y-6 z-10 rounded-lg',
                    inSidebar
                        ? 'left-0 top-full mt-2 border border-[#DCE5F2] shadow-lg'
                        : '-top-20 -left-20'
                )
            "
        >
            <div class="space-y-4">
                <label
                    :class="
                        clsx(
                            isDark ? 'text-[#FAFAFA]' : 'text-[#060606]',
                            'text-sm font-semibold opacity-60'
                        )
                    "
                    for="text-color"
                >
                    Cor do texto
                </label>

                <div class="flex flex-col items-start space-y-2">
                    <button
                        @click="unsetColor"
                        :class="clsx('button-color', isDark ? 'dark' : 'light')"
                    >
                        <span class="flex items-center justify-center px-2 rounded">A</span>

                        <p>Padrão</p>
                    </button>

                    <button
                        v-for="(color, index) in colors"
                        :key="index"
                        @click="setColor(color.value)"
                        :class="
                            clsx(
                                'button-color',
                                editor.isActive('textStyle', { color: color.value }) && 'is-active',
                                isDark ? 'dark' : 'light'
                            )
                        "
                    >
                        <span
                            :class="clsx('flex items-center justify-center px-2 rounded')"
                            :style="{ color: color.value }"
                        >
                            A
                        </span>

                        <p>
                            {{ color.name }}
                        </p>
                    </button>
                </div>
            </div>

            <div class="space-y-4">
                <label
                    :class="
                        clsx(
                            isDark ? 'text-[#FAFAFA]' : 'text-[#060606]',
                            'text-sm font-semibold opacity-60'
                        )
                    "
                    for="text-color"
                >
                    Marca texto
                </label>

                <div class="flex flex-col items-start space-y-2">
                    <button
                        @click="unsetHighlight"
                        :class="clsx('button-color', isDark ? 'dark' : 'light')"
                    >
                        <span class="flex items-center justify-center px-2 rounded">A</span>

                        <p>Padrão</p>
                    </button>

                    <button
                        v-for="(color, index) in colors"
                        :key="index"
                        @click="setHighlight(color.value)"
                        :class="
                            clsx(
                                'button-color',
                                editor.isActive('textStyle', { color: color.value }) && 'is-active',
                                isDark ? 'dark' : 'light'
                            )
                        "
                    >
                        <span
                            :class="clsx('flex items-center justify-center px-2 rounded')"
                            :style="{ backgroundColor: color.value }"
                        >
                            A
                        </span>

                        <p>
                            {{ color.name }}
                        </p>
                    </button>
                </div>
            </div>
        </popover-panel>
    </popover>
</template>

<style scoped>
.button-color {
    @apply flex items-center text-base font-normal space-x-2;
}

.button-color.dark {
    @apply text-[#FAFAFA];
}

.button-color.light {
    @apply text-[#060606];
}
</style>
